"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _Events = require("../constants/Events");
var _CallbackTypes = _interopRequireDefault(require("../constants/CallbackTypes"));
var _LoggingUtil = _interopRequireDefault(require("../util/LoggingUtil"));
var _StylesUtil = require("../util/StylesUtil");
var _TimeUtil = _interopRequireDefault(require("../util/TimeUtil"));
var _ViewMode = require("../constants/ViewMode");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); } /*************************************************************************
* ADOBE CONFIDENTIAL
* ___________________
*
* Copyright 2020 Adobe
* All Rights Reserved.
*
* NOTICE: All information contained herein is, and remains
* the property of Adobe and its suppliers, if any. The intellectual
* and technical concepts contained herein are proprietary to Adobe
* and its suppliers and are protected by all applicable intellectual
* property laws, including trade secret and copyright laws.
* Dissemination of this information or reproduction of this material
* is strictly forbidden unless prior written permission is obtained
* from Adobe.
**************************************************************************/
(0, _StylesUtil.createStyles)();
const hostAppStartTime = (0, _TimeUtil.default)();
class ViewSDKInterfaceApp {
  constructor(config, serviceFactory) {
    this._serviceFactory = serviceFactory;
    this._appInitializeReceivedOn = new Date().getTime();
    this._initConfig(config);
    this._logExternal("Initializing Adobe DC View SDK.");
    this._initServices();
  }

  /**
   * @method
   * @description
   * previewFile(fileInfo, previewConfig) method to preview/embed pdf file.
   *
   * @param {object} fileInfo - file information to preview.
   * @param {object} inConfig - config to control the UI of file preview.
   * @return {Promise} - promise that resolves to PreviewRef when PDF is rendered.
   */
  previewFile(fileInfo, inConfig) {
    this._logExternal("Starting process to preview file.", fileInfo.metaData, inConfig);
    this._serviceFactory.getService("ValidationService").validateFileInfo(fileInfo);
    const embedMode = inConfig.embedMode ? inConfig.embedMode.toUpperCase() : _ViewMode.Preset.FULL_WINDOW;
    const previewConfig = _objectSpread(_objectSpread({}, inConfig), {}, {
      embedMode
    });
    this._serviceFactory.getService("ValidationService").validatePreviewConfig(previewConfig);
    this._serviceFactory.getService("ValidationService").validateFileInfoForPreviewConfig(fileInfo, previewConfig);
    const timeInfo = {
      actionReceivedOn: new Date().getTime(),
      hostAppStartTime,
      appStartTime: this._appInitializeReceivedOn,
      appInitializeReceivedOn: this._appInitializeReceivedOn
    };
    const divElement = window.document.getElementById(this._config.divId);
    if (divElement.internallyCreated && previewConfig.embedMode !== _ViewMode.Preset.LIGHT_BOX) {
      divElement.parentNode.removeChild(divElement);
      throw new Error(`DivId ${this._config.divId} is not present in HTML document.`);
    }
    return this._serviceFactory.getService("EmbedModeService").previewFile(this._config, fileInfo, previewConfig, timeInfo);
  }

  /**
   * @method
   * @description
   * By registering callbacks with specified parameters, workflows can be controlled related to
   * saving files, user profiles, user settings, and so on
   *
   * @param {string} type - type of callback
   * @param {Function} callback.
   * @param {object} options - parameters for extra information
   */
  registerCallback(type, callback, options) {
    this._logExternal(`Registering Callback for type : ${type}`);
    this._serviceFactory.getService("ValidationService").validateCallback(type, callback, options);
    if (type === _CallbackTypes.default.EVENT_LISTENER) {
      this._serviceFactory.getService("EventHandlerService").setEventListener(callback, options);
    } else {
      this._serviceFactory.getService("CallbackService").registerCallback(type, callback, options);
    }
  }
  _sendCallbackOptionsUpdateEvent(type) {
    switch (type) {
      case _CallbackTypes.default.SAVE_API:
        this._serviceFactory.getService("EventsService").sendEvent(_Events.InternalEvents.POLLING_CONFIG_UPDATE);
        break;
      default:
        break;
    }
  }

  /**
   * @method
   * @description
   * updateCallbackOptions method to validate and update callback options. This method also sends an options
   * update internal event
   *
   * @param {string} type - type of callback
   * @param {object} options - new callback options
   */
  updateCallbackOptions(type, options) {
    this._serviceFactory.getService("ValidationService").validateCallback(type, () => {}, options);
    this._serviceFactory.getService("CallbackService").updateCallbackOptions(type, options);
    this._sendCallbackOptionsUpdateEvent(type);
  }

  /**
   * @method
   * @description
   * method associated with document loading in the browser
   * @param {Number} loaded - percentage of file loading
   * @param {Number} total - total file size
   */
  fileProgressLoadingData(loaded, total) {
    this._serviceFactory.getService("AppStore").setFileLoadingProgress(loaded, total);
  }
  unMountViewerNode() {
    return this._serviceFactory.getService("EmbedModeService").unMountApp();
  }
  _initServices() {
    this._serviceFactory.getService("LoggingService").initialize(this._config.logToConsole);
    this._serviceFactory.getService("FullScreenManager").initialize(this._config, this._serviceFactory);
    this._config.locale = this._serviceFactory.getService("LocaleService").getLocale();
  }
  _logExternal(...args) {
    if (this._config.logToConsole) {
      (0, _LoggingUtil.default)(...args);
    }
  }
  _initConfig(config) {
    this._serviceFactory.getService("ValidationService").validateConfig(config);
    this._config = this._getFinalConfig(config);
    this._createInternalDivIfNotPresent();
  }

  /**
   * @method
   * @description
   * if it's not present in window it creates the internal div with the same id
   * and render the file view into it
   */
  _createInternalDivIfNotPresent() {
    let divElement = window.document.getElementById(this._config.divId);
    if (!divElement) {
      divElement = window.document.createElement("div");
      divElement.id = this._config.divId;
      divElement.internallyCreated = true;
      window.document.body.appendChild(divElement);
    }
  }
  _getFinalConfig(config) {
    const INITIAL_CONFIG = {
      divId: "adobe-dc-view",
      logToConsole: false,
      downloadWithCredentials: false
    };
    const finalConfig = _objectSpread(_objectSpread({}, INITIAL_CONFIG), config);
    finalConfig.serverEnv = "prod";
    finalConfig.viewSDKAppVersion = "";
    return finalConfig;
  }
}
exports.default = ViewSDKInterfaceApp;